// Backend API endpoint for generating debate arguments
// This keeps the OpenAI API key secure on the server instead of exposing it in the frontend
//
// For local development (Django): Set REACT_APP_BACKEND_API_URL=http://localhost:8000/api in .env
// For production (Firebase): Set REACT_APP_BACKEND_API_URL=https://us-central1-ai-verbatim.cloudfunctions.net
const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8000/api';

if (!BACKEND_API_URL) {
  console.error('REACT_APP_BACKEND_API_URL is not set. API calls will fail.');
}

// Determine the correct endpoint based on backend type
// Firebase Functions use /generateArgument, Django uses /generate-argument/
const isFirebase = BACKEND_API_URL.includes('cloudfunctions.net');
const GENERATE_ARGUMENT_ENDPOINT = isFirebase ? '/generateArgument' : '/generate-argument/';

export async function generateArgument(
  topic: string,
  stance: number,
  rhetoricStyle: string,
  systemPrompt: string,
  previousArguments: Array<{ speaker: string; text: string; summary?: string }>
): Promise<{ text: string; summary: string }> {
  const url = `${BACKEND_API_URL}${GENERATE_ARGUMENT_ENDPOINT}`;
  console.log('Calling backend API at:', url);
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        topic,
        stance,
        rhetoricStyle,
        systemPrompt,
        previousArguments
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('Backend API error:', response.status, errorData);
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      text: data.text || "An error occurred while generating the argument.",
      summary: data.summary || "Error generating response."
    };
  } catch (error: any) {
    console.error('Error generating argument:', error);
    
    // Provide more helpful error messages
    if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
      console.error('Cannot connect to backend. Make sure:');
      console.error('1. Backend server is running on', BACKEND_API_URL);
      console.error('2. CORS is configured in Django settings');
      console.error('3. The backend URL is correct in your .env file');
    }
    
    return {
      text: "An error occurred while generating the argument. Please check that the backend server is running.",
      summary: "Error generating response."
    };
  }
}
